import { zodResolver } from "@hookform/resolvers/zod";
import {
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormHelperText,
  TextField,
} from "@mui/material";
import { Stack } from "@mui/system";
import "ace-builds/src-noconflict/ext-language_tools";
import "ace-builds/src-noconflict/mode-lua";
import "ace-builds/src-noconflict/theme-dracula";
import "ace-builds/webpack-resolver";
import { useAtom } from "jotai/index";
import React, { useEffect, useMemo, useRef } from "react";
import AceEditor from "react-ace";
import { Controller, FieldError, useForm } from "react-hook-form";
import { CheckboxElement, TextFieldElement } from "react-hook-form-mui";
import { useTranslation } from "react-i18next";
import { z } from "zod";

import { useAuthGroupsQuery } from "@/api/hooks/auth-groups-hooks";
import { BaseAutocomplete } from "@/components/a-autocomplete";
import {
  gameType,
  room,
  sizeRange,
  technology,
} from "@/components/config-modals/router-edit-modal/constants";
import {
  SELECT_RAW_STATUS,
  SELECT_RAW_TYPE,
  SELECT_RAW_VISIBLE_FIELDS,
  SelectRawData,
  SelectRawOptionalField,
  SelectRawType,
} from "@/components/config-modals/select-raw-edit-modal/constants";
import CustomSelectOption from "@/components/custom-select-option";
import { ModalActionsButtons } from "@/components/modal-actions-buttons";
import { isOwner } from "@/lib/auth-predicates";
import { formatLua } from "@/lib/common-utils";
import { SysCfgCreateRequestDto, SysCfgRegularDto } from "@/shared/api";
import { meStore } from "@/store/store";
import { MakeOptional } from "@/types/common-types";

const SelectRawWriteModal = ({
  isOpen,
  mode,
  onCancel,
  onSave,
  defaultValues,
  confirmLoading,
  disabled = false,
  title,
}: {
  isOpen: boolean;
  mode: "add" | "edit";
  onCancel: () => void;
  onSave: (config: SysCfgCreateRequestDto) => void;
  defaultValues?: MakeOptional<SysCfgRegularDto, "id" | "created_at" | "updated_at">;
  confirmLoading?: boolean;
  disabled?: boolean;
  title?: string;
}) => {
  const { t } = useTranslation();
  const [me] = useAtom(meStore);
  const scriptEditorRef = useRef<AceEditor | null>(null);

  const roomOptions = useMemo(() => room.map((it) => it.value), []);
  const technologyOptions = useMemo(() => technology.map((it) => it.value), []);
  const gameTypeOptions = useMemo(() => gameType.map((it) => it.value), []);

  const validateEditorContent = () => {
    const errors = scriptEditorRef.current?.editor.getSession().getAnnotations();
    return !(errors && errors.length > 0);
  };

  const schema = useMemo(() => {
    const optionalEnum = <T extends readonly [string, ...string[]]>(values: T) =>
      z.union([z.enum(values), z.literal("")]).optional();

    return z.object({
      name: z.string().min(1),
      auth_groups: isOwner(me)
        ? z.array(z.string())
        : z.array(z.string()).min(1, t("mustContainsGroup")),
      enabled: z.boolean().default(true),
      type: z.enum(SELECT_RAW_TYPE, { required_error: t("fieldRequired") }),
      status: optionalEnum(SELECT_RAW_STATUS),
      room: z.string().optional(),
      technology: z.string().optional(),
      game_type: z.string().optional(),
      game_group: z.string().optional(),
      seats: z.preprocess(
        (val) => (val === "" || val == null || Number.isNaN(val) ? undefined : val),
        z.coerce
          .number({ invalid_type_error: t("enterOnlyNumber") })
          .int()
          .min(sizeRange[0])
          .max(sizeRange[1])
          .optional()
      ),
      rakeback: z.preprocess(
        (val) => (val === "" || val == null || Number.isNaN(val) ? undefined : val),
        z.coerce.number({ invalid_type_error: t("enterOnlyNumber") }).optional()
      ),
      limit: z
        .array(z.coerce.number({ invalid_type_error: t("enterOnlyNumber") }))
        .optional()
        .default([]),
      weight: z.coerce.number({
        invalid_type_error: t("enterOnlyNumber"),
        required_error: t("fieldRequired"),
      }),
      script: z
        .string()
        .min(1, t("fieldRequired"))
        .refine(() => validateEditorContent(), "Fix errors in editor"),
    });
  }, [me, t]);

  type Form = z.infer<typeof schema>;

  const { data: authGroupsRs, isLoading: authGroupsIsLoading } = useAuthGroupsQuery({
    enabled: isOpen,
  });

  const authGroups = useMemo(() => {
    return authGroupsRs?.data ?? [];
  }, [authGroupsRs]);

  const initData = useMemo<Partial<Form>>(() => {
    let groups = defaultValues ? Array.from(defaultValues.auth_groups ?? []) : [];

    if (authGroups.length === 1 && !isOwner(me)) {
      groups = authGroups.map((it) => it.name);
    }

    const selectRawData = (defaultValues?.data ?? {}) as SelectRawData;

    return defaultValues
      ? {
          name: defaultValues.name,
          enabled: defaultValues.enabled ?? true,
          auth_groups: groups,
          type: selectRawData.type,
          status: selectRawData.status,
          room: selectRawData.room ?? "",
          technology: selectRawData.technology ?? "",
          game_type: selectRawData.game_type ?? "",
          game_group: selectRawData.game_group ?? "",
          seats: selectRawData.seats,
          rakeback: selectRawData.rakeback,
          limit: selectRawData.limit ?? [],
          weight: selectRawData.weight ?? 0,
          script: selectRawData.script ?? "",
        }
      : {
          auth_groups: groups,
          enabled: true,
          game_group: "",
          weight: 0,
          script: "",
          room: "",
          technology: "",
          game_type: "",
          status: "",
          limit: [],
        };
  }, [defaultValues, authGroups, me]);

  const {
    control,
    reset,
    getValues,
    setValue,
    watch,
    handleSubmit,
    formState: { errors, isDirty },
  } = useForm<Form>({
    mode: "onChange",
    disabled: disabled,
    resolver: zodResolver(schema),
    defaultValues: initData,
  });

  const selectedType = watch("type") as SelectRawType | undefined;
  const visibleFields = useMemo<Set<SelectRawOptionalField>>(() => {
    if (!selectedType || !(selectedType in SELECT_RAW_VISIBLE_FIELDS)) {
      return new Set();
    }
    return new Set(SELECT_RAW_VISIBLE_FIELDS[selectedType]);
  }, [selectedType]);

  const isFieldVisible = (field: SelectRawOptionalField) => visibleFields.has(field);

  const submit = handleSubmit(async (data) => {
    const script = await formatLua(data.script);
    const emptyToUndefined = (value?: string) => (value ? value : undefined);
    const fields = SELECT_RAW_VISIBLE_FIELDS[data.type] ?? [];

    onSave({
      name: data.name,
      enabled: data.enabled,
      auth_groups: new Set(data.auth_groups),
      data: {
        type: data.type,
        weight: data.weight,
        script,
        ...(fields.includes("status") ? { status: emptyToUndefined(data.status) } : {}),
        ...(fields.includes("room") ? { room: emptyToUndefined(data.room) } : {}),
        ...(fields.includes("technology")
          ? { technology: emptyToUndefined(data.technology) }
          : {}),
        ...(fields.includes("game_type")
          ? { game_type: emptyToUndefined(data.game_type) }
          : {}),
        ...(fields.includes("game_group")
          ? { game_group: emptyToUndefined(data.game_group) }
          : {}),
        ...(fields.includes("seats") ? { seats: data.seats } : {}),
        ...(fields.includes("rakeback") ? { rakeback: data.rakeback } : {}),
        ...(fields.includes("limit") ? { limit: data.limit ?? [] } : {}),
      },
    });
  });

  useEffect(() => {
    reset({ ...initData });

    const script = initData.script ?? "";
    if (!script) return;

    let cancelled = false;
    void formatLua(script).then((formatted) => {
      if (!cancelled && formatted !== getValues("script")) {
        setValue("script", formatted, { shouldDirty: false });
      }
    });

    return () => {
      cancelled = true;
    };
  }, [initData, isOpen, reset, getValues, setValue]);

  const renderSingleSelect = ({
    name,
    label,
    options,
    required = false,
  }: {
    name: keyof Form;
    label: string;
    options: string[];
    required?: boolean;
  }) => (
    <Controller
      control={control}
      name={name}
      render={({ field, fieldState: { error } }) => (
        <BaseAutocomplete
          {...field}
          disabled={disabled}
          value={field.value === "" || field.value == null ? null : String(field.value)}
          onChange={(_, data) => field.onChange(data ?? "")}
          options={options}
          renderInput={(params) => (
            <TextField
              {...params}
              variant="standard"
              required={required}
              label={label}
              error={!!error}
              helperText={error?.message ?? " "}
            />
          )}
        />
      )}
    />
  );

  return (
    <Dialog
      open={isOpen}
      maxWidth="lg"
      onClose={(_event, reason) => {
        if (
          reason === "backdropClick" &&
          isDirty &&
          !window.confirm("Data has been changed. Do you want discard changes?")
        )
          return;
        onCancel?.();
      }}
    >
      <DialogTitle>
        {mode === "add"
          ? title ?? t("addNewConfig")
          : t("editConfig", { name: initData.name })}
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ paddingTop: "5px", width: "600px" }}>
          <TextFieldElement
            control={control}
            required
            name="name"
            label={t("name")}
            error={!!errors.name}
            helperText={errors.name?.message ?? " "}
            variant="standard"
            slotProps={{
              input: {
                readOnly: mode === "edit",
              },
            }}
          />

          <Controller
            control={control}
            name="auth_groups"
            rules={{
              required: !isOwner(me) ? "field is required" : undefined,
            }}
            render={({ field, fieldState: { error } }) => (
              <BaseAutocomplete
                {...field}
                multiple
                disabled={disabled || (authGroupsRs?.data.length === 1 && !isOwner(me))}
                loading={authGroupsIsLoading}
                value={field.value}
                onChange={(_, data) => field.onChange(data)}
                options={
                  authGroupsRs?.data?.map((i) => i.name).sort((a, b) => a.localeCompare(b)) ??
                  []
                }
                disableCloseOnSelect
                renderOption={(props, option, { selected }) => (
                  <CustomSelectOption label={option} selected={selected} props={props} />
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    variant="standard"
                    label={!isOwner(me) ? t("authGroupsRequired") : t("authGroups")}
                    placeholder={t("authGroups")}
                    error={!!error}
                    helperText={error?.message}
                  />
                )}
              />
            )}
          />

          {mode === "add" && (
            <CheckboxElement
              control={control}
              name="enabled"
              label={t("enabled")}
              disabled={disabled}
            />
          )}

          {renderSingleSelect({
            name: "type",
            label: t("type"),
            options: [...SELECT_RAW_TYPE],
            required: true,
          })}

          {isFieldVisible("status") &&
            renderSingleSelect({
              name: "status",
              label: t("status"),
              options: [...SELECT_RAW_STATUS],
            })}

          {isFieldVisible("room") &&
            renderSingleSelect({
              name: "room",
              label: t("room"),
              options: roomOptions,
            })}

          {isFieldVisible("technology") &&
            renderSingleSelect({
              name: "technology",
              label: t("technology"),
              options: technologyOptions,
            })}

          {isFieldVisible("game_type") &&
            renderSingleSelect({
              name: "game_type",
              label: t("gameType"),
              options: gameTypeOptions,
            })}

          {isFieldVisible("game_group") && (
            <TextFieldElement
              control={control}
              name="game_group"
              label={t("gameGroup")}
              error={!!errors.game_group}
              helperText={errors.game_group?.message ?? " "}
              variant="standard"
            />
          )}

          {isFieldVisible("seats") && (
            <TextFieldElement
              control={control}
              name="seats"
              label={t("seats")}
              type="number"
              error={!!errors.seats}
              helperText={errors.seats?.message ?? " "}
              variant="standard"
            />
          )}

          <TextFieldElement
            control={control}
            required
            name="weight"
            label={t("weight")}
            type="number"
            error={!!errors.weight}
            helperText={errors.weight?.message ?? " "}
            variant="standard"
          />

          {isFieldVisible("rakeback") && (
            <TextFieldElement
              control={control}
              name="rakeback"
              label={t("rakeback")}
              type="number"
              slotProps={{ htmlInput: { step: "any" } }}
              error={!!errors.rakeback}
              helperText={errors.rakeback?.message ?? " "}
              variant="standard"
            />
          )}

          {isFieldVisible("limit") && (
            <Controller
              name="limit"
              control={control}
              rules={{
                required: t("fieldRequired"),
                validate: (v) =>
                  v?.filter((a) => isNaN(a)).length === 0 || t("enterOnlyNumber"),
                pattern: {
                  value: /^[0-9]+$/,
                  message: t("enterOnlyNumber"),
                },
              }}
              render={({ field: { onChange, onBlur, value, ref }, fieldState: { error } }) => (
                <BaseAutocomplete
                  multiple
                  freeSolo
                  options={[]}
                  value={value}
                  disabled={disabled}
                  onChange={(_event, newValue) => {
                    onChange(newValue);

                    setValue(
                      "limit",
                      newValue.map((v) => parseInt(v.toString()))
                    );
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      type="number"
                      variant="outlined"
                      label={t("limits")}
                      placeholder={t("addTag")}
                      inputRef={ref}
                      onBlur={onBlur}
                      error={!!error}
                      helperText={
                        (error as FieldError[] | undefined)?.filter((el) => !!el?.message)?.[0]
                          ?.message
                      }
                    />
                  )}
                  renderValue={(value, getItemProps) =>
                    value.map((option, index) => (
                      <Chip label={option} {...getItemProps({ index })} key={index} />
                    ))
                  }
                />
              )}
            />
          )}

          <Controller
            control={control}
            name="script"
            render={({ field }) => (
              <FormControl
                error={!!errors.script}
                sx={{
                  ...(!!errors.script && {
                    borderColor: "red",
                    borderWidth: "3px",
                    borderStyle: "solid",
                  }),
                }}
              >
                <p>{t("script")}</p>
                {errors.script?.message && (
                  <FormHelperText>{errors.script.message}</FormHelperText>
                )}
                <AceEditor
                  onChange={field.onChange}
                  onBlur={() => {
                    void formatLua(field.value ?? "").then((formatted) => {
                      if (formatted !== field.value) {
                        field.onChange(formatted);
                      }
                      field.onBlur();
                    });
                  }}
                  value={field.value}
                  readOnly={disabled}
                  ref={scriptEditorRef}
                  mode="lua"
                  theme="dracula"
                  width="100%"
                  setOptions={{
                    enableBasicAutocompletion: true,
                    enableLiveAutocompletion: true,
                    tabSize: 2,
                    useSoftTabs: true,
                  }}
                />
              </FormControl>
            )}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <ModalActionsButtons
          onCancelClick={onCancel}
          onConfirmClick={submit}
          loading={confirmLoading}
        />
      </DialogActions>
    </Dialog>
  );
};

export default SelectRawWriteModal;
